import {useInfiniteQuery} from "@tanstack/react-query";

import {ParamsOptions} from "@/src/modules/core/types";
import {ITEMS_PER_PAGE} from "@/src/modules/core/utils/constants";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";

import {buildHouseholdListFilter} from "../lib/household-list-filter";
import {householdService} from "../service";

type UseHouseholdsOptions = ParamsOptions & {
  /** When set, scopes queries to this property instead of the header-selected property. */
  contextPropertyId?: number;
};

type UseHouseholdsQueryOptions = {
  enabled?: boolean;
};

export function useHouseholds(
  options: UseHouseholdsOptions = {},
  infiniteQueryOptions: UseHouseholdsQueryOptions = {},
) {
  const {enabled = true} = infiniteQueryOptions;
  const userProperty = useStoredUserProperty();
  const {contextPropertyId, filter = {}, ...rest} = options;
  const filterWithContext = buildHouseholdListFilter(
    filter as Record<string, unknown>,
    contextPropertyId ?? userProperty?.id ?? 0,
  );
  const requestOptions = {...rest, filter: filterWithContext};

  const {data, ...query} = useInfiniteQuery({
    queryKey: ["households", requestOptions],
    enabled,
    queryFn: ({pageParam = 0}) => {
      const page = {
        limit: ITEMS_PER_PAGE,
        offset: pageParam,
      };
      return householdService.getHouseholds({...requestOptions, page});
    },
    getNextPageParam: (lastPage, pages) => {
      const loadedItems = pages.reduce((acc, page) => acc + page.data.length, 0);
      return loadedItems < lastPage.meta.total ? loadedItems : undefined;
    },
    initialPageParam: 0,
    gcTime: 0,
    staleTime: 0,
  });

  const households = data?.pages.flatMap((page) => page.data) ?? [];

  return {
    households,
    ...query,
  };
}

// type UseCustomInfiniteQueryProps<TData, TError> = {
//   queryKey: string[];
//   options?: ParamsOptions;
//   queryFn: (context: {pageParam: number}) => Promise<TData>;
//   getNextPageParam: (lastPage: TData, pages: TData[]) => number | undefined;
// } & Parameters<typeof useInfiniteQuery>[0];

// function useCustomInfiniteQuery<TData, TError>({
//   queryKey,
//   queryFn,
//   getNextPageParam,
//   initialPageParam,
//   ...restOptions
// }: UseCustomInfiniteQueryProps<TData, TError>) {
//   const {data, ...query} = useInfiniteQuery({
//     queryKey,
//     queryFn,
//     getNextPageParam: (lastPage, pages) => {
//       const loadedItems = pages.reduce((acc, page) => acc + page.data.length, 0);
//       return loadedItems < lastPage.meta.total ? loadedItems : undefined;
//     },
//     initialPageParam: 0,
//   });
// }
